import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, DoCheck, effect, Inject, isDevMode, OnDestroy, OnInit } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorTrackerService } from 'app/behavior-tracking/behavior-tracker.service';
import { RxWebReactiveFormConfigService } from 'app/core/services/rx-web-reactive-form-config.service';
import { SESSION_IDLE_CONFIG } from 'app/session-idle/config/session-idle.config';
import { SessionIdleService } from 'app/session-idle/session-idle.service';
import { MfaDialogService } from 'app/shared/components/mfa-dialog/mfa-dialog.service';
import { MfaService } from 'app/shared/components/mfa-dialog/services/mfa.service';
import { NotificationType } from 'app/shared/models/notification-type';
import { NotificationService } from 'app/shared/services/notification/notification.service';
import { SiteConfig } from 'config/site-config';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { environment } from 'environments/environment';
import MobileDetect from 'mobile-detect';
import { of, Subject, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { AppStateService } from './app-state.service';
import { AppState } from './app.state';
import { AuthenticationService } from './core/services/authentication/authentication.service';
import { SpinnerService } from './core/services/spinner/spinner.service';
import { VersionCheckService } from './core/services/version/version-check.service';
import { IAppState } from './shared/models/app-state.interface';
import { UtilityService } from './shared/services/utility/utility.service';
import { SupportScriptService } from './support-scripts/support-script.service';

dayjs.extend(relativeTime);

@Component({
    selector: 'upsc-root',
    templateUrl: './app.component.html',
    // styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, DoCheck, OnDestroy {
    public isPageLoading = false;
    // country: string;
    // public isEndpointsReady = false;
    public isEndpointsReady = true;
    public lang: string[];
    public destroy$ = new Subject<void>();
    protected readonly isDevMode = isDevMode;
    protected readonly environment = environment;

    public constructor(private router: Router,
                       private metaTagService: Meta,
                       private utilityService: UtilityService,
                       private spinnerService: SpinnerService,
                       private versionCheckService: VersionCheckService,
                       private cdRef: ChangeDetectorRef,
                       private translateService: TranslateService,
                       @Inject(DOCUMENT) private document: Document,
                       private readonly authenticationService: AuthenticationService,
                       private readonly appStateService: AppStateService,
                       private readonly supportScriptService: SupportScriptService,
                       private readonly reactiveFormConfigService: RxWebReactiveFormConfigService,
                       private readonly appState: AppState,
                       private readonly behaviorTracker: BehaviorTrackerService,
                       private readonly sessionIdleService: SessionIdleService,
                       private readonly notificationService: NotificationService,
                       private readonly mfaService: MfaService,
                       private readonly mfaDialogService: MfaDialogService,
                       private readonly storage: StorageMap,
    ) {
        this.startSessionIdleService();

        this.router.events.subscribe(event => this.navigationInterceptor(event));

        this.translateService.addLangs(['de', 'en', 'en-gb','es', 'fr', 'it', 'ja', 'th', 'zh-hk']);
        this.translateService.setDefaultLang('en');
        const navigatorLanguage = this.utilityService.getSiteLanguage() || window.navigator.language;
        this.storage.get('lang', { type: 'string' }).subscribe(
            (lang) => {
                const language = lang || navigatorLanguage || 'en';
                this.changeLanguage(language);

                // MV3-4409 - Cookie settings should change based on language
                this.metaTagService.updateTag({
                    name: 'DCSext.pCC',
                    content: this.utilityService.getCookieCountryCode(language),
                });
                this.metaTagService.updateTag({
                    name: 'DCSext.pLL',
                    content: this.utilityService.getCookieLanguageCode(language),
                });
            },
        );

        this.reactiveFormConfigService.init();
        this.initializeSession();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public ngOnInit(): void {
        this.spinnerService.spinnerStatus
            .subscribe(
                (status) => {
                    this.isPageLoading = status;
                },
            );

        // this.setHtmlEnvironments();

        // [MV3-2732] initialize version check.
        if (environment.production && SiteConfig.reload.polling.enable) {
            this.versionCheckService.initVersionCheck('/version-hash.json', SiteConfig.reload.polling.interval);
        }
    }

    public ngDoCheck(): void {
        this.cdRef.detectChanges();
    }

    // public handleInactivityCallback(): void {
    //     if (!SiteConfig.reload.idle.enable) {
    //         return;
    //     }
    //
    //     this.router.navigate(['/logout']).then();
    //     // Sign out current user or display a specific message regarding inactivity
    //     // location.reload(true);
    // }

    public changeLanguage(selectedLanguage: string): void {
        let usedLanguage = selectedLanguage?.toLowerCase();
        // this.translateService.setDefaultLang(usedLanguage);
        this.translateService.use(usedLanguage)
            .pipe(
                catchError((err) => {
                    console.error(err);
                    usedLanguage = 'en';
                    return this.translateService.use('en');
                }),
                finalize(() => {
                    sessionStorage.setItem('lang', usedLanguage);
                    sessionStorage.setItem('site-locale', usedLanguage);
                }),
            )
            .subscribe(() => {
            });
    }

    private startSessionIdleService(): void {
        if (!environment.feature?.sessionIdle) {
            return;
        }

        effect(() => {
            if (this.authenticationService.isLoggedInSignal()) {
                this.sessionIdleService.start();
                return;
            }

            this.sessionIdleService.stop();
        }, {
            allowSignalWrites: true,
        });

        effect(() => {
            if (!this.authenticationService.isLoggedInSignal()) {
                return;
            }

            if (this.sessionIdleService.isIdle()) {
                this.sessionIdleService.stop();
                // console.log('[Session] Idle timeout occurred. Logging out.');
                this.authenticationService.logout(true, this.sessionIdleService.logoutReason).then();
                // this.notificationService.notify(
                //     'We logged you out for security. You can sign back in to continue where you left off.',
                //     'Your session has expired',
                //     NotificationType.INFO,
                // );
            }
        }, {
            allowSignalWrites: true,
        });

        effect(() => {
            if (this.sessionIdleService.showWarning()) {
                // console.log('[Session] Warning: approaching timeout...');
                const timeDiffSeconds = SESSION_IDLE_CONFIG.idleTimeoutTimeInSeconds - SESSION_IDLE_CONFIG.idleWarningTimeInSeconds;
                const relativeTime = dayjs().add(timeDiffSeconds, 'seconds').fromNow();
                this.notificationService.notify(
                    `For your security, we will log you out ${ relativeTime }.`,
                    'You have been inactive for a while',
                    NotificationType.WARNING,
                );
            }
        });
    }

    // Single startup hydration: one login check, then shared AppStateService state (deduped with the route guard).
    private initializeSession(): void {
        this.authenticationService.isLoggedIn()
            .pipe(
                take(1),
                switchMap(isLoggedIn => isLoggedIn
                    ? this.appStateService.getAppState().pipe(defaultIfEmpty(null as IAppState))
                    : of(null as IAppState)),
                catchError((error) => {
                    if (this.isSessionLookupAuthError(error)) {
                        return of(null as IAppState);
                    }

                    return throwError(() => error);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((state) => {
                if (state?.user) {
                    this.supportScriptService.loadUserSupportScript(state.user);
                    return;
                }

                this.supportScriptService.loadDefaultScript();
            });
    }

    private checkForMfa(): void {
        const mfaFeatureConfig = environment.feature?.mfa;
        if (!mfaFeatureConfig?.enabled
            || !mfaFeatureConfig?.allowedCountries?.includes(this.appState?.user$()?.CountryCode?.toUpperCase())) {
            return;
        }

        this.mfaService.isEnrolled()
            .pipe(
                takeUntil(this.destroy$),
            )
            .subscribe(
                (isEnrolled) => {
                    if (isEnrolled) {
                        return;
                    }

                    this.mfaDialogService.open({
                        context: 'enroll',
                    });
                },
            );
    }

    private isSessionLookupAuthError(error: any): boolean {
        return error?.status === 401 || error?.status === 403;
    }

    private navigationInterceptor(event): void {
        if (event instanceof NavigationStart) {
            this.showSpinner();
        }

        if (event instanceof NavigationEnd) {
            // this.behaviorTracker.capture('page_loaded', {
            //     path: event.urlAfterRedirects,
            //     timestamp: Date.now(),
            // });
            this.hideSpinner();
        }

        if (event instanceof NavigationCancel) {
            this.hideSpinner();
        }

        if (event instanceof NavigationError) {
            this.hideSpinner();
        }
    }

    private showSpinner(): void {
        this.isPageLoading = true;
    }

    private hideSpinner(): void {
        this.utilityService.delay(
            () => {
                this.isPageLoading = false;

                const bodyEl = this.document.getElementsByTagName('body')[0];
                bodyEl?.classList.remove('noscroll');
            },
            250);
    }

    private setHtmlEnvironments() {
        const md = new MobileDetect(window.navigator.userAgent);

        const htmlEl = this.document.getElementsByTagName('html')[0];

        if (md.os()) {
            htmlEl.classList.add(md.os());
        }
    }
}
